import React from 'react';
import { Button } from '../../../components/Bootstrap';
import { VenueInput } from './../../../globalTypes';
import { Form, NobleModal } from '../../../components';
import { VenueQuery_venue as Venue, VenueQuery_menus as Menu } from './__generated__/VenueQuery';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../config';
import { RevokeAccessMutationVariables } from './__generated__/RevokeAccessMutation';
import { QueueSyncMutation, QueueSyncMutationVariables } from './__generated__/QueueSyncMutation';
import { formatInTimeZone } from 'date-fns-tz';
import { MutationResult } from 'react-apollo';

interface SquareSetupSectionProps {
  venue: Venue;
  menus?: Menu[];
  posSetupConfig?: any;
  editing: boolean;
  revokePOSAccess({ variables }: { variables: RevokeAccessMutationVariables });
  onSubmit(data: VenueInput);
  queueSync({ variables }: { variables: QueueSyncMutationVariables });
  queueSyncStatus?: MutationResult<QueueSyncMutation>;
  handleCancel();
}

export const SquareSetupSection: React.FC<SquareSetupSectionProps> = (props) => {
  const { queueSyncStatus, venue, queueSync, onSubmit, revokePOSAccess } = props;
  const squareState = React.useRef(uuidv4());
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [relinkModalOpen, setRelinkModalOpen] = React.useState(false);

  React.useEffect(() => {
    window.localStorage.setItem('squareVenueId', venue.id);
    window.localStorage.setItem('squareState', squareState.current);
  }, [venue]);

  const onSubmitWrapped = React.useCallback(
    ({ formData }) => {
      const { primaryLocationId, menuLocationIds, menuId } = formData;

      onSubmit({
        squareConfig: {
          primaryLocationId,
          menuLocationIds,
          menuId,
        },
      });
    },
    [onSubmit]
  );

  const onClickRevoke = React.useCallback(() => {
    revokePOSAccess({
      variables: {
        venueId: venue.id,
        posType: 'square',
      },
    });
  }, [revokePOSAccess, venue]);

  const toggleConfirmModal = React.useCallback(() => {
    setConfirmModalOpen(!confirmModalOpen);
  }, [setConfirmModalOpen, confirmModalOpen]);

  const toggleRelinkModal = React.useCallback(() => {
    setRelinkModalOpen(!relinkModalOpen);
  }, [setRelinkModalOpen, relinkModalOpen]);

  const onClickQueueSync = React.useCallback(() => {
    queueSync({
      variables: {
        venueId: venue.id,
      },
    });
  }, [queueSync, venue]);

  const { menus } = props;
  const selectedMenu = React.useMemo(() => {
    return menus.find((m) => m.id === `${venue.posConfig?.menuId}`);
  }, [menus, venue]);

  if (!props.venue.posConfig) {
    return (
      <div>
        <p className='manage-venue-copy'>
          <Button href={`${config.SQUARE_AUTH_URL}&state=${squareState.current}`} color='primary'>
            Link with Square
          </Button>
        </p>
      </div>
    );
  } else if (!props.editing) {
    let menuLocationText = 'None Selected';
    const locationsByIds = props.posSetupConfig?.locations.reduce((accum, location) => {
      accum[location.id] = location.name;
      return accum;
    }, {});

    if (props.venue.posConfig?.menuLocationIds) {
      menuLocationText = props.venue.posConfig.menuLocationIds
        .map((id) => {
          return locationsByIds[id];
        })
        .join(', ');
    }

    let orderLocationText = 'None Selected';
    if (props.venue.posConfig?.primaryLocationId) {
      orderLocationText = locationsByIds[props.venue.posConfig?.primaryLocationId];
    }

    return (
      <div>
        <p className='manage-venue-copy'>Location to route orders to: {orderLocationText}</p>
        <p className='manage-venue-copy'>Locations to pull items from: {menuLocationText}</p>
        <p className='manage-venue-copy'>Menu to sync categories with: {selectedMenu?.name ?? 'None'}</p>

        <div className='d-flex align-items-bottom mt-5'>
          <div>
            <Button disabled={queueSyncStatus?.loading} onClick={onClickQueueSync} color='primary' className='me-3'>
              Queue Menu Sync
            </Button>
          </div>
          <div>
            <p className='me-3'>
              {venue.posLastSyncTime ? (
                `Menu last updated from Square on ${formatInTimeZone(
                  venue.posLastSyncTime,
                  venue.timezone,
                  'MMM. d, yyyy h:mm aaa'
                )}`
              ) : (
                <em>Noble has not synced with Square yet.</em>
              )}
            </p>
            <div>
              {queueSyncStatus?.loading && <p className='text-italic'>Loading...</p>}
              {queueSyncStatus?.data && (
                <p className='text-primary'>
                  POS Sync Queued! You should expect to see your menu items updated in the next 5 minutes.
                </p>
              )}
              {queueSyncStatus?.error && (
                <p className='text-error'>
                  We're sorry there was an issue starting the POS sync. Our team has been notified.
                </p>
              )}
            </div>
          </div>
        </div>
        <div>
          <Button onClick={toggleRelinkModal} color='primary' className='mt-5 me-5'>
            Re-Link with Square
          </Button>
          <Button onClick={toggleConfirmModal} className='mt-5' color='danger'>
            Revoke Access
          </Button>
        </div>
        <NobleModal isOpen={confirmModalOpen} toggle={toggleConfirmModal}>
          <NobleModal.Title title='Revoke Square Access' />
          <NobleModal.Body>
            <p className='text-center'>
              Revoking access to Square will prevent inventory from being synced and prevent any future orders.
            </p>
            <div className='mt-5 text-center'>
              <Button onClick={toggleConfirmModal} color='link'>
                Cancel
              </Button>
              <Button className='ms-2 ps-3 pe-3' onClick={onClickRevoke} color='danger'>
                Revoke
              </Button>
            </div>
          </NobleModal.Body>
        </NobleModal>
        <NobleModal isOpen={relinkModalOpen} toggle={toggleRelinkModal}>
          <NobleModal.Title title='Re-link Square' />
          <NobleModal.Body>
            <p className='text-center'>
              Re-linking will allow Noble to access new parts of your Square system. After re-linking, you will have to
              choose your locations again. Your menu and items pulled from Square will <b>NOT</b> be affected.
            </p>
            <div className='mt-5 text-center'>
              <Button onClick={toggleRelinkModal} color='link'>
                Cancel
              </Button>
              <Button
                className='ms-2 ps-3 pe-3'
                href={`${config.SQUARE_AUTH_URL}&state=${squareState.current}`}
                color='primary'
              >
                Re-link
              </Button>
            </div>
          </NobleModal.Body>
        </NobleModal>
      </div>
    );
  } else {
    const locations = props.posSetupConfig?.locations ?? [];
    const schema = {
      type: 'object',
      properties: {
        primaryLocationId: {
          id: '/properties/locations',
          title: 'Location to route orders to',
          type: 'string',
          enum: locations.map((location) => location.id),
          enumNames: locations.map((location) => location.name),
        },
        menuLocationIds: {
          id: '/properties/locations',
          type: 'array',
          title: 'Locations to pull items from',
          items: {
            type: 'string',
            enum: locations.map((location) => location.id),
            enumNames: locations.map((location) => location.name),
          },
        },
        menuId: {
          id: '/properties/menuId',
          type: 'string',
          title: 'Menu to sync categories with',
          enum: props.menus.map((menu) => menu.id),
          enumNames: props.menus.map((menu) => menu.name),
        },
      },
    };

    const formData = {
      primaryLocationId: venue.posConfig.primaryLocationId,
      menuLocationIds: venue.posConfig.menuLocationIds ?? [],
      menuId: venue.posConfig.menuId,
    };

    return (
      <Form onSubmit={onSubmitWrapped} formData={formData} schema={schema}>
        <div>
          <Button onClick={props.handleCancel} color='link'>
            Cancel
          </Button>
          <Button type='submit' color='primary' className='btn--rounded-padded'>
            Submit
          </Button>
        </div>
      </Form>
    );
  }
};
